import Phaser from "phaser";
import { config, WORLD } from "@/config";
import { activeJwt } from "@/auth/session";
import { createSocket, type SocketHandle } from "@/net/socket";
import { joinZone, type ZoneChannelHandle } from "@/net/zone-channel";
import type {
  ChatEvent,
  EmoteEvent,
  NPCMovedEvent,
  PlayerJoinedEvent,
  PlayerLeftEvent,
  PlayerMovedEvent,
  ZoneSnapshot,
} from "@/net/types";
import { Avatars } from "@/world/objects/Avatars";
import { currentProjection, defaultIsoOrigin, MAP_SIZE, setCurrentProjection, type ScreenPos } from "@/world/map/Iso";
import { projectionFor } from "@/world/map/projection";
import { buildZoneMap } from "@/world/map/ZoneMapAdapter";
import { getZoneConfig, type ZoneConfig, type ZoneTransition } from "@/world/map/ZoneManifest";
import type { ZoneGrid } from "@/net/types";
import { routeToZone } from "@/scenes/routing";
import { Bots, parseBotCount } from "@/world/objects/Bots";
import { SheepNPC } from "@/world/objects/SheepNPC";
import { MoveController } from "@/interact/MoveController";
import { ChatInput } from "@/chat/ChatInput";
import { GroupChat } from "@/chat/GroupChat";
import { LeftSidebar } from "@/ui/LeftSidebar";
import { PlayerList } from "@/chat/PlayerList";
import { StatsPanel } from "@/ui/StatsPanel";
import { InteractionSystem } from "@/interact/InteractionSystem";
import { resumeAudio, playFootstep, playChatReceive, playPlayerJoin, playPlayerLeave, playBump } from "@/audio/SoundManager";
import { MusicPlayer } from "@/audio/MusicPlayer";
import { MusicPlayerPanel } from "@/ui/MusicPlayerPanel";
import { DayNightCycle } from "@/world/DayNightCycle";
import type { CollisionMap } from "@/world/map/CollisionMap";
import { CollisionDebugOverlay } from "@/world/map/CollisionDebugOverlay";
import { OccupancyMap } from "@/world/map/OccupancyMap";
import { VoiceConnection } from "@/audio/VoiceConnection";
import { LiveKitMusicBroadcaster } from "@/audio/LiveKitMusicBroadcaster";

export class ZoneScene extends Phaser.Scene {
  static readonly KEY = "ZoneScene";

  private socket?: SocketHandle;
  private channel?: ZoneChannelHandle;
  private voice?: VoiceConnection;
  private avatars?: Avatars;
  private bots?: Bots;
  private sheep?: SheepNPC;
  private moveCtrl?: MoveController;
  private interaction?: InteractionSystem;
  private chatInput = new ChatInput();
  private groupChat = new GroupChat();
  private sidebar = new LeftSidebar();
  private playerList = new PlayerList();
  private statsPanel = new StatsPanel();
  private musicPlayer?: MusicPlayer;
  private musicPanel?: MusicPlayerPanel;
  private musicBroadcaster?: LiveKitMusicBroadcaster;
  private dayNight?: DayNightCycle;
  private collision?: CollisionMap;
  private collisionOverlay?: CollisionDebugOverlay;
  private occupancy = new OccupancyMap();
  private origin: ScreenPos = { sx: 0, sy: 0 };
  private fpsText?: Phaser.GameObjects.Text;
  private zoneTransitions: ZoneTransition[] = [];
  private transitionBubble: Phaser.GameObjects.Text | undefined;
  private activeTransition: ZoneTransition | undefined;

  private selfId = "";
  private cameraFollowing = false;
  private lastMoveSentAt = 0;
  private lastSentIntent = { dx: 0, dy: 0, dir: 0 };
  private hud = document.getElementById("hud");
  private botCount = 0;
  private selfPos = { x: 8, y: 8 };
  private gridSize = MAP_SIZE;
  private hasReceivedInitialSnapshot = false;
  private spawnOverride: [number, number] | undefined;
  private fKey?: Phaser.Input.Keyboard.Key;
  private zoneConfig: ZoneConfig | undefined;

  constructor() {
    super(ZoneScene.KEY);
  }

  init(data?: { targetSpawn?: [number, number] }): void {
    this.spawnOverride = data?.targetSpawn;
    this.hasReceivedInitialSnapshot = false;
    this.cameraFollowing = false;
    this.activeTransition = undefined;
    this.transitionBubble = undefined;
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#0e1116");
    this.input.once("pointerdown", () => {
      resumeAudio();
      this.musicPlayer = new MusicPlayer();
      this.musicPanel!.setMusicPlayer(this.musicPlayer);
      // Voice may have started already (joinedZone races pointerdown);
      // if so, hook the jukebox broadcaster now that musicPlayer exists.
      this.attachMusicBroadcaster();
    });

    try {
      this.zoneConfig = getZoneConfig(this, config.zoneId);
    } catch {
      // Manifest not loaded or zone missing — legacy fallback
    }

    // Activate the zone's projection BEFORE any rendering or origin math.
    // All Iso.* facade exports delegate to this projection for the rest of the scene.
    // Side-scroll zones are routed to SideScrollScene at boot, so they never reach
    // here — but narrow the union just in case.
    const projKind = this.zoneConfig?.projection === "sidescroll" ? "iso" : this.zoneConfig?.projection;
    setCurrentProjection(projectionFor(projKind));

    this.gridSize = this.zoneConfig?.gridSize ?? MAP_SIZE;
    this.origin = defaultIsoOrigin(this.scale.width, this.scale.height, this.gridSize);

    this.fKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.F);

    // Map + collision + interaction are built later, in `setupZone`, once
    // the server-authoritative ZoneGrid arrives on the channel join reply.
    this.selfId = readSelfIdFromJwt(activeJwt) ?? localId();
    this.avatars = new Avatars(this, this.selfId, this.origin);
    this.musicPanel = new MusicPlayerPanel();

    this.sidebar.setChangeHandler((c) => {
      const self = this.avatars?.self();
      if (!self) return;
      self.setColor(c.color);
      self.setLabel(c.name);
      self.setAvatarType(c.avatarType);
      this.groupChat.setPlayerName(this.selfId, c.name);
    });

    this.botCount = parseBotCount();
    if (this.botCount > 0) {
      this.bots = new Bots(this.botCount, this.avatars);
    }

    this.fpsText = this.add
      .text(this.scale.width - 8, 8, "", {
        fontSize: "11px",
        color: "#888888",
        backgroundColor: "rgba(0,0,0,0.4)",
        padding: { left: 5, right: 5, top: 2, bottom: 2 },
      })
      .setOrigin(1, 0)
      .setDepth(1000000)
      .setScrollFactor(0);

    this.dayNight = new DayNightCycle(this);

    // Local echo so chat bubbles work even without a server connection.
    // Overridden by the real send handler once connected.
    this.chatInput.setSendHandler((text) => {
      const sprite = this.avatars?.self();
      sprite?.showChat(this, text);
      this.groupChat.addMessage(this.selfId, text);
      this.statsPanel.incrementMessages();
    });

    this.connect().catch((err) => {
      console.error("connect failed", err);
      this.setHud(`disconnected — ${stringifyError(err)}`);
    });
  }

  update(_time: number, delta: number): void {
    this.avatars?.tick(delta);
    this.bots?.tick(delta);
    this.sheep?.tick(delta);
    this.dayNight?.tick(delta);
    this.collisionOverlay?.tick();
    this.maybeSendMove(delta);
    this.interaction?.update(this.selfPos.x, this.selfPos.y, this.chatInput.active);
    this.checkTransitionApproach();
    this.maybeActivateTransition();
    this.updateFps();

    // Drive the listener-position into the spatial-audio controller so
    // PannerNode distance attenuation reflects self motion. (Remote
    // speaker positions are pushed via `onPlayerMoved`.)
    const sc = this.voice?.getSpatialController();
    if (sc) {
      sc.selfX = this.selfPos.x;
      sc.selfY = this.selfPos.y;
    }
  }

  shutdown(): void {
    this.teardown();
  }

  private async connect(): Promise<void> {
    if (this.botCount > 0) {
      // Stress mode runs without a socket. Synthesize a degraded
      // ZoneGrid so setupZone has something to build from.
      this.setupZone(stubZoneGrid(this.gridSize, this.zoneConfig?.spawnPoint));
      this.chatInput.setSendHandler((text) => {
        const sprite = this.avatars?.self();
        sprite?.showChat(this, text);
        this.groupChat.addMessage(this.selfId, text);
        this.statsPanel.incrementMessages();
      });
      this.setHud(`stress mode — ${this.botCount} local bots, no socket`);
      return;
    }
    if (!activeJwt) {
      this.setHud("not authenticated — please log in");
      return;
    }

    this.socket = createSocket(activeJwt);
    this.socket.connect();

    this.channel = joinZone(this.socket.socket, config.zoneId, config.zoneInstance, {
      onSnapshot: (snap) => this.onSnapshot(snap),
      onPlayerJoined: (e) => this.onPlayerJoined(e),
      onPlayerLeft: (e) => this.onPlayerLeft(e),
      onPlayerMoved: (e) => this.onPlayerMoved(e),
      onChat: (e) => this.onChat(e),
      onEmote: (e) => this.onEmote(e),
      onNPCMoved: (e) => this.onNPCMoved(e),
      onMusicPlaying: (e) => this.playerList.setMusicPlaying(e.user_id, e.track_name, e.playlist),
      onMusicStopped: (e) => this.playerList.setMusicStopped(e.user_id),
      onError: (reason) => this.setHud(`channel error: ${stringifyError(reason)}`),
    });

    const { instance, user_id, zone_grid } = await this.channel.join();
    this.selfId = user_id;
    this.avatars?.setSelfId(user_id);
    this.playerList.setSelfId(user_id);
    this.groupChat.setSelfId(user_id);

    this.setupZone(zone_grid);

    this.chatInput.setSendHandler((text) => {
      this.channel?.sendChat(text);
      // Local echo for immediate feedback
      const sprite = this.avatars?.self();
      sprite?.showChat(this, text);
      this.groupChat.addMessage(this.selfId, text);
      this.statsPanel.incrementMessages();
    });
    this.playerList.startPolling();
    this.applySelfCustomization();
    this.statsPanel.setConnected(config.zoneId, instance);
    this.setHud(`zone:${config.zoneId}:${instance} — self=${this.selfId.slice(0, 6)}`);

    this.voice = new VoiceConnection({
      socket: this.socket.socket,
      zoneId: config.zoneId,
      instance,
      onVoiceState: (userId, active) => this.playerList.setVoiceActive(userId, active),
      onLevel: (rms) => this.sidebar.voiceMeter.handleLevel(rms),
      onSpeakingChange: (speaking, mode) =>
        this.sidebar.voiceMeter.handleSpeakingChange(speaking, mode),
    });
    const voice = this.voice;
    voice
      .start()
      .then(() => {
        this.sidebar.voiceMeter.bindControl(voice);
        this.attachMusicBroadcaster();
      })
      .catch((err) => console.warn("voice start failed", err));
  }

  /**
   * Builds the visual map + collision + interactables from the
   * server-authoritative `ZoneGrid`. Called once per scene lifecycle
   * after the channel join reply arrives.
   */
  private setupZone(zoneGrid: ZoneGrid): void {
    const { map: isoMap, transitions, spawnPoint } = buildZoneMap(
      this,
      this.origin,
      this.zoneConfig,
      zoneGrid,
    );
    this.zoneTransitions = transitions;
    const spawn = this.spawnOverride ?? spawnPoint;
    this.selfPos = { x: spawn[0], y: spawn[1] };
    this.collision = isoMap.collision;
    this.collisionOverlay = new CollisionDebugOverlay(this, this.origin, this.collision);

    this.moveCtrl = new MoveController(this, this.origin, this.collision);
    this.moveCtrl.onClickTarget = (wx, wy) => this.showClickMarker(wx, wy);

    const isIso = currentProjection().kind === "iso";
    this.interaction = new InteractionSystem(this, this.origin);
    this.interaction.setTrees(isoMap.trees);
    this.interaction.setHeightMap(isoMap.heightMap);
    if (isIso) {
      this.interaction.spawnFire();
    }
    this.interaction.setMilestoneHandler((tag) => this.claimMilestone(tag));

    this.avatars?.upsert(this.selfId, { x: this.selfPos.x, y: this.selfPos.y, dir: 0 });

    if (isIso) {
      this.sheep = new SheepNPC(this, this.origin, this.collision, this.occupancy);
    }

    this.applySelfCustomization();
    this.startCameraFollow();
  }

  /**
   * Wires the jukebox broadcaster onto the same LiveKit Room as the
   * voice mic. Called after `voice.start()` resolves so the Room is
   * connected. No-op if music isn't enabled yet (the player constructs
   * lazily on first interaction).
   */
  private attachMusicBroadcaster(): void {
    if (this.musicBroadcaster) return;
    const room = this.voice?.getRoom();
    if (!room || !this.musicPlayer || !this.channel) return;

    this.musicBroadcaster = new LiveKitMusicBroadcaster({
      player: this.musicPlayer,
      room,
      channel: this.channel,
      selfId: this.selfId,
    });
  }

  private applySelfCustomization(): void {
    const self = this.avatars?.self();
    if (!self) return;
    const c = this.sidebar.customization;
    self.setColor(c.color);
    self.setLabel(c.name);
    self.setAvatarType(c.avatarType);
    this.groupChat.setPlayerName(this.selfId, c.name);
  }

  private showClickMarker(worldX: number, worldY: number): void {
    const projection = currentProjection();
    const { sx, sy } = projection.worldToScreen(worldX, worldY);
    const halfW = projection.tileWidth / 2;
    const halfH = projection.tileHeight / 2;
    const marker = this.add.graphics();
    marker.lineStyle(2, 0xffffff, 0.8);
    marker.beginPath();
    marker.moveTo(0, -6);
    marker.lineTo(8, 0);
    marker.lineTo(0, 6);
    marker.lineTo(-8, 0);
    marker.closePath();
    marker.strokePath();
    // Centre marker on tile centre regardless of projection.
    const centreX = this.origin.sx + sx + (projection.kind === "iso" ? 0 : halfW);
    const centreY = this.origin.sy + sy + (projection.kind === "iso" ? halfH : halfH);
    marker.setPosition(centreX, centreY);
    marker.setDepth(999999);

    this.tweens.add({
      targets: marker,
      alpha: 0,
      scale: 1.5,
      duration: 500,
      ease: "Quad.easeOut",
      onComplete: () => marker.destroy(),
    });
  }

  private startCameraFollow(): void {
    if (this.cameraFollowing) return;
    const self = this.avatars?.self();
    if (!self) return;

    this.cameras.main.startFollow(self.container, true, 0.1, 0.1);
    this.cameraFollowing = true;
  }

  private checkTransitionApproach(): void {
    if (this.zoneTransitions.length === 0) return;
    const { x, y } = this.selfPos;
    let nearest: ZoneTransition | undefined;
    let nearestDist = Infinity;

    for (const tr of this.zoneTransitions) {
      const pos = tr.dockPosition ?? tr.bridgePosition;
      if (!pos) continue;
      const dx = x - pos[0];
      const dy = y - pos[1];
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < tr.approachRadius && dist < nearestDist) {
        nearest = tr;
        nearestDist = dist;
      }
    }

    if (nearest && nearest !== this.activeTransition) {
      this.activeTransition = nearest;
      this.showTransitionBubble(nearest);
    } else if (!nearest && this.activeTransition) {
      this.activeTransition = undefined;
      this.hideTransitionBubble();
    }
  }

  private maybeActivateTransition(): void {
    if (!this.activeTransition || !this.fKey) return;
    if (!Phaser.Input.Keyboard.JustDown(this.fKey)) return;
    if (this.chatInput.active) return;
    const tr = this.activeTransition;
    this.hideTransitionBubble();
    routeToZone(this, tr.targetZone, tr.targetSpawn);
  }

  private showTransitionBubble(tr: ZoneTransition): void {
    this.hideTransitionBubble();
    const pos = tr.dockPosition ?? tr.bridgePosition;
    if (!pos) return;
    const projection = currentProjection();
    const { sx, sy } = projection.worldToScreen(pos[0], pos[1]);
    const halfW = projection.tileWidth / 2;
    this.transitionBubble = this.add.text(
      this.origin.sx + sx + halfW,
      this.origin.sy + sy - 28,
      `${tr.label}\n[F] to travel`,
      {
        fontSize: "11px",
        color: "#ffffff",
        backgroundColor: "rgba(0,0,0,0.7)",
        padding: { left: 8, right: 8, top: 4, bottom: 4 },
        align: "center",
      },
    );
    this.transitionBubble.setOrigin(0.5, 1);
    this.transitionBubble.setDepth(999999);
  }

  private hideTransitionBubble(): void {
    this.transitionBubble?.destroy();
    this.transitionBubble = undefined;
  }

  private teardown(): void {
    this.hideTransitionBubble();
    this.bots?.clear();
    this.sheep?.destroy();
    this.dayNight?.destroy();
    this.collisionOverlay?.destroy();
    this.playerList.stopPolling();
    this.statsPanel.stop();
    this.musicBroadcaster?.destroy();
    delete this.musicBroadcaster;
    void this.voice?.stop();
    this.channel?.leave().catch(() => undefined);
    void this.socket?.disconnect();
    this.avatars?.clear();
  }

  private maybeSendMove(delta: number): void {
    if (!this.moveCtrl || this.chatInput.active) return;

    const intent = this.moveCtrl.sample(this.selfPos.x, this.selfPos.y);
    // Cap delta to prevent frame-spike jumps (tab switch, GC pause, etc.)
    const dt = Math.min(delta, 50);

    if (intent.dx !== 0 || intent.dy !== 0) {
      const speed = 0.004; // tiles/ms
      const candidateX = this.selfPos.x + intent.dx * speed * dt;
      const candidateY = this.selfPos.y + intent.dy * speed * dt;

      let newX = this.selfPos.x;
      let newY = this.selfPos.y;

      if (this.collision) {
        // Axis-separated collision with edge-aware checks (wall sliding)
        if (!this.collision.isStepBlocked(this.selfPos.x, this.selfPos.y, candidateX, candidateY)) {
          newX = candidateX;
          newY = candidateY;
        } else if (!this.collision.isStepBlocked(this.selfPos.x, this.selfPos.y, candidateX, this.selfPos.y)) {
          newX = candidateX;
        } else if (!this.collision.isStepBlocked(this.selfPos.x, this.selfPos.y, this.selfPos.x, candidateY)) {
          newY = candidateY;
        } else {
          // Fully blocked on both axes — bump feedback
          playBump();
          this.cameras.main.shake(80, 0.003);
        }
        // else: both axes blocked, don't move
      } else {
        newX = candidateX;
        newY = candidateY;
      }

      newX = Math.max(1, Math.min(this.gridSize - 2, newX));
      newY = Math.max(1, Math.min(this.gridSize - 2, newY));

      if (newX !== this.selfPos.x || newY !== this.selfPos.y) {
        this.selfPos.x = newX;
        this.selfPos.y = newY;
        this.avatars?.upsert(this.selfId, { x: this.selfPos.x, y: this.selfPos.y, dir: intent.dir });
        this.statsPanel.setPosition(this.selfPos.x, this.selfPos.y);
        playFootstep();
      }
    }

    if (!this.channel) return;
    const now = performance.now();
    if (now - this.lastMoveSentAt < WORLD.moveSendIntervalMs) return;

    const changed =
      intent.dx !== this.lastSentIntent.dx ||
      intent.dy !== this.lastSentIntent.dy ||
      intent.dir !== this.lastSentIntent.dir;

    if (!changed && intent.dx === 0 && intent.dy === 0) return;

    this.channel.sendMove(intent);
    this.lastSentIntent = intent;
    this.lastMoveSentAt = now;
  }

  private updateFps(): void {
    if (!this.fpsText) return;
    const fps = this.game.loop.actualFps;
    const ft = this.game.loop.delta;
    const avatarCount = this.botCount > 0 ? this.botCount : "live";
    this.fpsText.setText(`${fps.toFixed(0)} fps  ${ft.toFixed(1)} ms  [${avatarCount}]`);
  }

  private onSnapshot(snap: ZoneSnapshot): void {
    if (!this.avatars) return;
    const players = snap.players ?? [];
    const inSnapshot = new Set(players.map((p) => p.user_id));

    // Remove players who left and whose leave event we missed
    for (const id of this.avatars.userIds()) {
      if (!inSnapshot.has(id)) this.avatars.remove(id);
    }

    for (const p of players) {
      if (p.user_id === this.selfId) {
        if (!this.hasReceivedInitialSnapshot) {
          // First snapshot after join: adopt server position (may be cached from previous session)
          this.selfPos.x = p.x;
          this.selfPos.y = p.y;
          this.hasReceivedInitialSnapshot = true;
        }
        // Keep client-predicted position; periodic snapshots must not snap us back
        this.avatars.upsert(p.user_id, { x: this.selfPos.x, y: this.selfPos.y, dir: p.dir });
      } else {
        this.avatars.upsert(p.user_id, { x: p.x, y: p.y, dir: p.dir });
      }
    }

    this.applySelfCustomization();
    this.startCameraFollow();
    this.playerList.updatePlayers(this.avatars.userIds());
    this.statsPanel.setOnlineCount([...this.avatars.userIds()].length);

    // Sync server-authoritative NPCs
    if (snap.npcs) {
      this.sheep?.syncSnapshot(snap.npcs);
    }
  }

  private onPlayerJoined(e: PlayerJoinedEvent): void {
    this.avatars?.upsert(e.user_id, { x: 8, y: 8, dir: 0 });
    this.playerList.addPlayer(e.user_id);
    this.statsPanel.setOnlineCount(this.avatars ? [...this.avatars.userIds()].length : 0);
    playPlayerJoin();
  }

  private onPlayerLeft(e: PlayerLeftEvent): void {
    this.avatars?.remove(e.user_id);
    this.playerList.removePlayer(e.user_id);
    this.statsPanel.setOnlineCount(this.avatars ? [...this.avatars.userIds()].length : 0);
    playPlayerLeave();
  }

  private onPlayerMoved(e: PlayerMovedEvent): void {
    if (e.user_id === this.selfId) {
      // Self reconciliation. Client-side prediction moves the local
      // avatar at frame rate; server is authoritative. Pull self
      // toward server when drift exceeds a threshold (catches
      // teleports / massive desync); tolerate small drifts as latency.
      const dx = e.x - this.selfPos.x;
      const dy = e.y - this.selfPos.y;
      const dist = Math.hypot(dx, dy);

      if (dist > 1.0) {
        this.selfPos.x = e.x;
        this.selfPos.y = e.y;
        this.avatars?.upsert(this.selfId, { x: e.x, y: e.y, dir: e.dir });
        this.statsPanel.setPosition(this.selfPos.x, this.selfPos.y);
      } else if (dist > 0.25) {
        this.selfPos.x += dx * 0.5;
        this.selfPos.y += dy * 0.5;
        this.avatars?.upsert(this.selfId, { x: this.selfPos.x, y: this.selfPos.y, dir: e.dir });
        this.statsPanel.setPosition(this.selfPos.x, this.selfPos.y);
      }
      return;
    }
    this.avatars?.upsert(e.user_id, { x: e.x, y: e.y, dir: e.dir });
    this.voice?.getSpatialController()?.updateSpeakerPosition(e.user_id, e.x, e.y);
  }

  private onChat(e: ChatEvent): void {
    // Skip self — already shown via local echo in the send handler
    if (e.user_id === this.selfId) return;
    const sprite = this.avatars?.get(e.user_id);
    sprite?.showChat(this, e.text);
    this.groupChat.addMessage(e.user_id, e.text);
    this.statsPanel.incrementMessages();
    playChatReceive();
  }

  private onEmote(e: EmoteEvent): void {
    const sprite = this.avatars?.get(e.user_id);
    sprite?.showEmote(this, e.emote_id);
  }

  private onNPCMoved(e: NPCMovedEvent): void {
    this.sheep?.upsert(e.id, e.x, e.y, e.dir);
  }

  private setHud(text: string): void {
    if (this.hud) this.hud.textContent = text;
  }

  private async claimMilestone(tag: string): Promise<void> {
    if (!activeJwt) return;
    try {
      await fetch(`${config.apiUrl}/api/v1/milestones/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${activeJwt}`,
        },
        body: JSON.stringify({ tag }),
      });
    } catch {
      // silent — milestone will be retried on next threshold hit
    }
  }
}

function readSelfIdFromJwt(jwt: string): string | null {
  if (!jwt) return null;
  const parts = jwt.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload?.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

function localId(): string {
  return `local-${Math.random().toString(36).slice(2, 8)}`;
}

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/**
 * Degraded `ZoneGrid` for stress-test (`?bots=N`) mode, which runs
 * without a socket — bots don't collide and no terrain is rendered.
 */
function stubZoneGrid(size: number, spawn?: [number, number]): ZoneGrid {
  return {
    size,
    spawn_point: spawn ?? [size / 2, size / 2],
    kinds: [],
    height: [],
    trees: [],
    solid_props: [],
    blocked: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  };
}
